/**
 * CountryCurrencyContext
 * ======================
 * Shared state for the visitor's selected country and its currency.
 *
 *  - Auto-detects country via IP geolocation on first visit.
 *  - Persists the selection to localStorage so it survives reloads.
 *  - Fetches the **latest** exchange rate whenever the country changes.
 *  - Exposes formatting helpers (convertPrice, formatPrice) so any public
 *    page can display prices in the visitor's currency.
 *
 * Used by:
 *   - CountrySelector (PublicHeader) — writes the selection
 *   - Pricing page                   — reads currency + rate for price display
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchCurrencyData, fetchRateForCurrency } from '../utils/exchangeRateCache';

// ── Country → ISO 4217 currency code mapping ──────────────────────────
const COUNTRY_CURRENCY = {
  // East Africa
  UG: 'UGX', KE: 'KES', TZ: 'TZS', RW: 'RWF', BI: 'BIF', SS: 'SSP',
  ET: 'ETB', ER: 'ERN', DJ: 'DJF', SO: 'SOS',
  // Central & Southern Africa
  CD: 'CDF', CG: 'XAF', CF: 'XAF', TD: 'XAF', CM: 'XAF', GA: 'XAF',
  GQ: 'XAF', ST: 'STN', AO: 'AOA', ZM: 'ZMW', MW: 'MWK', MZ: 'MZN',
  ZW: 'ZWL', BW: 'BWP', NA: 'NAD', ZA: 'ZAR', LS: 'LSL', SZ: 'SZL',
  MG: 'MGA', MU: 'MUR', SC: 'SCR', KM: 'KMF',
  // West Africa
  NG: 'NGN', GH: 'GHS', CI: 'XOF', SN: 'XOF', ML: 'XOF', BF: 'XOF',
  NE: 'XOF', GN: 'GNF', GW: 'XOF', SL: 'SLL', LR: 'LRD', TG: 'XOF',
  BJ: 'XOF', GM: 'GMD', MR: 'MRU', CV: 'CVE',
  // North Africa
  EG: 'EGP', MA: 'MAD', DZ: 'DZD', TN: 'TND', LY: 'LYD', SD: 'SDG',
  // Middle East
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
  JO: 'JOD', LB: 'LBP', IL: 'ILS', PS: 'ILS', IQ: 'IQD', IR: 'IRR',
  TR: 'TRY', YE: 'YER',
  // Asia Pacific
  IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR', CN: 'CNY',
  JP: 'JPY', KR: 'KRW', SG: 'SGD', MY: 'MYR', TH: 'THB', PH: 'PHP',
  ID: 'IDR', VN: 'VND', KH: 'KHR', MM: 'MMK', LA: 'LAK',
  AU: 'AUD', NZ: 'NZD', FJ: 'FJD',
  // Europe
  GB: 'GBP', IE: 'EUR', FR: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR',
  PT: 'EUR', NL: 'EUR', BE: 'EUR', LU: 'EUR', AT: 'EUR', SE: 'SEK',
  NO: 'NOK', DK: 'DKK', FI: 'EUR', IS: 'ISK', PL: 'PLN', CZ: 'CZK',
  SK: 'EUR', HU: 'HUF', RO: 'RON', BG: 'BGN', GR: 'EUR', HR: 'EUR',
  RS: 'RSD', SI: 'EUR', BA: 'BAM', AL: 'ALL', MK: 'MKD', ME: 'EUR',
  XK: 'EUR', BY: 'BYN', MD: 'MDL', UA: 'UAH', RU: 'RUB', GE: 'GEL',
  AM: 'AMD', AZ: 'AZN', EE: 'EUR', LV: 'EUR', LT: 'EUR',
  CH: 'CHF',
  // Americas
  US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL', AR: 'ARS', CO: 'COP',
  PE: 'PEN', CL: 'CLP', VE: 'VES', EC: 'USD', BO: 'BOB', PY: 'PYG',
  UY: 'UYU', CU: 'CUP', DO: 'DOP', JM: 'JMD', TT: 'TTD', BB: 'BBD',
  BS: 'BSD', PA: 'PAB', CR: 'CRC', GT: 'GTQ', HN: 'HNL', SV: 'USD',
  NI: 'NIO',
};

// ── Currency symbols ──────────────────────────────────────────────────
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', CHF: 'Fr',
  INR: '₹', AUD: 'A$', NZD: 'NZ$', SGD: 'S$', HKD: 'HK$', KRW: '₩', THB: '฿',
  MYR: 'RM', IDR: 'Rp', PHP: '₱', VND: '₫', PKR: '₨', BDT: '৳', LKR: 'Rs',
  AED: 'د.إ', SAR: '﷼', QAR: 'ر.ق', KWD: 'د.ك', BHD: 'د.ب', OMR: 'ر.ع', JOD: 'د.أ', ILS: '₪', TRY: '₺',
  KES: 'KSh', UGX: 'USh', TZS: 'TSh', RWF: 'RF', NGN: '₦', GHS: 'GH₵', ZAR: 'R',
  EGP: 'E£', MAD: 'DH', ETB: 'Br', XOF: 'CFA', XAF: 'FCFA',
  CAD: 'C$', MXN: 'MX$', BRL: 'R$', ARS: 'AR$', COP: 'COL$', CLP: 'CLP$', PEN: 'S/',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei',
  BGN: 'лв', HRK: 'kn', UAH: '₴', RUB: '₽', ISK: 'kr',
  // Additional symbols for countries in the selector
  DJF: 'Fdj', SOS: 'So.Sh', SSP: 'SSP', ERN: 'Nfk', SLL: 'Le', LRD: 'L$',
  GMD: 'D', MRU: 'UM', CVE: '$', SDG: 'SDG', LYD: 'LD', IQD: 'ع.د', IRR: '﷼',
  YER: '﷼', NPR: 'Rs', KHR: '៛', MMK: 'K', LAK: '₭', FJD: 'FJ$',
  BYN: 'Br', MDL: 'L', RSD: 'din', BAM: 'KM', ALL: 'L', MKD: 'den',
  GEL: '₾', AMD: '֏', AZN: '₼', DOP: 'RD$', JMD: 'J$', TTD: 'TT$', BBD: 'Bds$',
  BSD: 'B$', PAB: 'B/.', CRC: '₡', GTQ: 'Q', HNL: 'L', NIO: 'C$',
  BOB: 'Bs', PYG: '₲', UYU: '$U', CUP: '$', VES: 'Bs', AOA: 'Kz',
  ZMW: 'ZK', MWK: 'MK', MZN: 'MT', ZWL: '$', BWP: 'P', NAD: 'N$',
  MGA: 'Ar', MUR: 'Rs', SCR: 'Rs', KMF: 'CF', STN: 'Db', TND: 'DT',
  DZD: 'DA', GTQ: 'Q', LBP: 'ل.ل', JOD: 'د.أ',
};

// ── Zero-decimal currencies (no fractional units in practice) ─────────
const ZERO_DECIMAL_CURRENCIES = new Set([
  'UGX', 'KES', 'TZS', 'RWF', 'NGN', 'GHS', 'XOF', 'XAF', 'ETB', 'EGP', 'MAD',
  'JPY', 'KRW', 'VND', 'IDR', 'PKR', 'BDT', 'LKR', 'PHP', 'KHR', 'LAK', 'MMK',
  'ISK', 'HUF', 'COP', 'CLP', 'PYG', 'UYU', 'DZD', 'TND', 'BIF', 'CDF', 'GMD',
  'GNF', 'KMF', 'MGA', 'MRO', 'MWK', 'RUB', 'SEK', 'NOK', 'DKK',
  'DJF', 'SOS', 'SSP', 'ERN', 'SLL', 'MRU', 'CVE', 'SDG', 'IQD', 'IRR',
  'YER', 'NPR', 'FJD', 'BYN', 'MDL', 'RSD', 'BAM', 'ALL', 'MKD',
  'DOP', 'JMD', 'TTD', 'BBD', 'BSD', 'PAB', 'CRC', 'HNL', 'NIO',
  'BOB', 'CUP', 'VES', 'AOA', 'ZMW', 'MZN', 'BWP', 'NAD', 'MUR',
  'SCR', 'KMF', 'STN', 'TND', 'LBP', 'AMD', 'AZN', 'GEL',
]);

const STORAGE_KEY = 'ospito_selected_country';

// ── Helpers ───────────────────────────────────────────────────────────
export const getCurrencyForCountry = (countryCode) => COUNTRY_CURRENCY[countryCode] || 'USD';
export const getCurrencySymbol = (currencyCode) => CURRENCY_SYMBOLS[currencyCode] || currencyCode;
export const isZeroDecimal = (currencyCode) => ZERO_DECIMAL_CURRENCIES.has(currencyCode);

// ── Context ───────────────────────────────────────────────────────────
const CountryCurrencyContext = createContext(null);

export const CountryCurrencyProvider = ({ children }) => {
  const [country, setCountryState] = useState(null);     // { name, code }
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [loading, setLoading] = useState(true);
  const [autoDetected, setAutoDetected] = useState(false);
  const rateFetchId = useRef(0);

  // Refs to access latest values inside the interval without re-subscribing
  const currencyRef = useRef('USD');
  const countryRef = useRef(null);
  useEffect(() => { currencyRef.current = currency; }, [currency]);
  useEffect(() => { countryRef.current = country; }, [country]);

  // Re-fetch the exchange rate for the currently selected currency.
  // Uses force: true to bypass the in-memory cache and always get the
  // latest rate from the API.
  const refreshRate = useCallback(async () => {
    const currentCurrency = currencyRef.current;
    if (!currentCurrency || currentCurrency === 'USD') return;
    const fetchId = ++rateFetchId.current;
    try {
      const rate = await fetchRateForCurrency(currentCurrency, { force: true });
      if (fetchId !== rateFetchId.current) return;
      if (rate !== null && rate !== undefined) {
        setExchangeRate(rate);
      }
    } catch { /* keep previous rate */ }
  }, []);

  // Apply a country selection: map to currency, fetch latest rate
  const applyCountry = useCallback(async (countryObj, isAuto = false) => {
    const currencyCode = getCurrencyForCountry(countryObj.code);
    setCountryState(countryObj);
    setAutoDetected(isAuto);

    // Persist
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(countryObj)); } catch { /* ignore */ }

    if (currencyCode === 'USD') {
      setCurrency('USD');
      setExchangeRate(1);
      return;
    }

    // Fetch the latest exchange rate — force: true bypasses the in-memory
    // cache so we always get the current rate from the API.
    const fetchId = ++rateFetchId.current;
    try {
      const rate = await fetchRateForCurrency(currencyCode, { force: true });
      if (fetchId !== rateFetchId.current) return; // a newer selection superseded us
      if (rate !== null && rate !== undefined) {
        setCurrency(currencyCode);
        setExchangeRate(rate);
      } else {
        // Rate fetch failed — fall back to USD
        setCurrency('USD');
        setExchangeRate(1);
      }
    } catch {
      if (fetchId !== rateFetchId.current) return;
      setCurrency('USD');
      setExchangeRate(1);
    }
  }, []);

  // On mount: load saved selection or auto-detect
  useEffect(() => {
    let cancelled = false;

    // 1. Check localStorage for a previous choice
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.code) {
          (async () => {
            await applyCountry(parsed, false);
            if (!cancelled) setLoading(false);
          })();
          return;
        }
      }
    } catch { /* ignore */ }

    // 2. No saved choice — auto-detect via IP geolocation
    (async () => {
      try {
        const data = await fetchCurrencyData();
        if (cancelled) return;
        if (data.countryCode) {
          await applyCountry({ name: data.country || data.countryCode, code: data.countryCode }, true);
        }
      } catch { /* fall through */ }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [applyCountry]);

  // Periodic refresh — re-fetch the exchange rate every 10 minutes so
  // prices stay accurate while the user browses.  Also refreshes when
  // the tab regains focus (user returns from another tab/app).
  useEffect(() => {
    const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

    const intervalId = setInterval(() => {
      refreshRate();
    }, REFRESH_INTERVAL);

    const handleVisibility = () => {
      if (!document.hidden) refreshRate();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshRate]);

  // Public setter — called by CountrySelector when user picks a country
  const selectCountry = useCallback((countryObj) => {
    applyCountry(countryObj, false);
  }, [applyCountry]);

  // Price helpers
  const convertPrice = useCallback((usdPrice) => {
    const converted = usdPrice * exchangeRate;
    if (ZERO_DECIMAL_CURRENCIES.has(currency)) {
      return Math.round(converted);
    }
    return converted % 1 === 0 ? Math.round(converted) : Math.round(converted * 10) / 10;
  }, [currency, exchangeRate]);

  const formatPrice = useCallback((usdPrice) => {
    const converted = convertPrice(usdPrice);
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol} ${converted.toLocaleString()}`;
  }, [convertPrice, currency]);

  const value = {
    country,
    currency,
    exchangeRate,
    loading,
    autoDetected,
    selectCountry,
    refreshRate,
    convertPrice,
    formatPrice,
    getCurrencySymbol,
  };

  return (
    <CountryCurrencyContext.Provider value={value}>
      {children}
    </CountryCurrencyContext.Provider>
  );
};

export const useCountryCurrency = () => {
  const ctx = useContext(CountryCurrencyContext);
  if (!ctx) {
    throw new Error('useCountryCurrency must be used within CountryCurrencyProvider');
  }
  return ctx;
};

export { CURRENCY_SYMBOLS, ZERO_DECIMAL_CURRENCIES, COUNTRY_CURRENCY };
